/**
 * Content Service — CRUD for daily pregnancy content in Firestore.
 *
 * Firestore path:  pregnancyContent/{audience}/days/{docId}
 * docId format:    3-digit zero-padded day number, e.g. "001", "028", "280"
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  getDayInfo,
  dayNumberToDocId,
} from '@/config/pregnancyCalendar';
import type { Audience, UserRef } from '@/types/auth.types';
import type {
  DailyContent,
  DailyContentInput,
  DayProgress,
} from '@/types/content.types';

// ─── Path helpers ──────────────────────────────────────────────────────────────

function audienceCol(audience: Audience) {
  return collection(db, 'pregnancyContent', audience, 'days');
}

function dayDoc(audience: Audience, dayNumber: number) {
  return doc(db, 'pregnancyContent', audience, 'days', dayNumberToDocId(dayNumber));
}

// ─── Firestore → DailyContent converter ───────────────────────────────────────

function docToDailyContent(data: Record<string, unknown>): DailyContent {
  const toDate = (v: unknown): Date | null => {
    if (!v) return null;
    if (v instanceof Timestamp) return v.toDate();
    if (v instanceof Date) return v;
    return null;
  };

  return {
    audience:         data.audience as Audience,
    dayNumber:        data.dayNumber as number,
    weekNumber:       data.weekNumber as number,
    monthNumber:      data.monthNumber as number,

    title:            (data.title as string | null) ?? null,
    description:      (data.description as string | null) ?? null,
    meditation:       (data.meditation as string | null) ?? null,
    prayer:           (data.prayer as string | null) ?? null,
    bibleVerse:       (data.bibleVerse as string | null) ?? null,
    bibleReference:   (data.bibleReference as string | null) ?? null,

    titleMl:          (data.titleMl as string | null) ?? null,
    descriptionMl:    (data.descriptionMl as string | null) ?? null,
    meditationMl:     (data.meditationMl as string | null) ?? null,
    prayerMl:         (data.prayerMl as string | null) ?? null,
    bibleVerseMl:     (data.bibleVerseMl as string | null) ?? null,
    bibleReferenceMl: (data.bibleReferenceMl as string | null) ?? null,

    youtubeUrl:       (data.youtubeUrl as string | null) ?? null,
    audioUrl:         (data.audioUrl as string | null) ?? null,
    audioStoragePath: (data.audioStoragePath as string | null) ?? null,
    imageUrl:         (data.imageUrl as string | null) ?? null,
    imageStoragePath: (data.imageStoragePath as string | null) ?? null,

    status:    (data.status as DailyContent['status']) ?? 'not_started',

    createdBy: (data.createdBy as UserRef | null) ?? null,
    createdAt: toDate(data.createdAt),
    updatedBy: (data.updatedBy as UserRef | null) ?? null,
    updatedAt: toDate(data.updatedAt),
  };
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Fetch a single day's content. Returns null if the document doesn't exist yet.
 */
export async function getDayContent(
  audience: Audience,
  dayNumber: number,
): Promise<DailyContent | null> {
  const snap = await getDoc(dayDoc(audience, dayNumber));
  if (!snap.exists()) return null;
  return docToDailyContent(snap.data() as Record<string, unknown>);
}

/**
 * Save (create or merge-update) a day's content.
 * Always writes identity fields (audience, dayNumber, weekNumber, monthNumber)
 * to ensure the document is self-describing.
 */
export async function saveDayContent(
  audience: Audience,
  dayNumber: number,
  input: DailyContentInput,
  userRef: UserRef,
  isNew: boolean,
): Promise<void> {
  const info = getDayInfo(dayNumber);
  if (!info) throw new Error(`Invalid day number: ${dayNumber}`);

  const ref  = dayDoc(audience, dayNumber);
  const now  = serverTimestamp();

  const payload: Record<string, unknown> = {
    // Identity — always written
    audience,
    dayNumber,
    weekNumber:  info.weekNumber,
    monthNumber: info.monthNumber,

    // Content fields — only write what's provided
    ...input,

    // Audit
    updatedBy: userRef,
    updatedAt: now,
  };

  if (isNew) {
    payload.createdBy = userRef;
    payload.createdAt = now;
  }

  await setDoc(ref, payload, { merge: true });
}

/**
 * Delete a day's content document entirely.
 */
export async function deleteDayContent(
  audience: Audience,
  dayNumber: number,
): Promise<void> {
  await deleteDoc(dayDoc(audience, dayNumber));
}

/**
 * Load all day statuses for an audience.
 * Queries every document in pregnancyContent/{audience}/days.
 * Returns a map: dayNumber → ContentStatus.
 * Days with no document are implicitly 'not_started'.
 */
export async function getAudienceDayStatuses(
  audience: Audience,
): Promise<Map<number, DailyContent['status']>> {
  const snap = await getDocs(audienceCol(audience));
  const map  = new Map<number, DailyContent['status']>();
  snap.forEach(d => {
    const data = d.data() as Record<string, unknown>;
    const day  = data.dayNumber as number;
    const status = (data.status as DailyContent['status']) ?? 'not_started';
    map.set(day, status);
  });
  return map;
}

/**
 * Convenience: returns DayProgress[] for a specific month
 * from an already-loaded status map.
 */
export function extractMonthProgress(
  statusMap: Map<number, DailyContent['status']>,
  dayStart: number,
  dayEnd: number,
): DayProgress[] {
  const result: DayProgress[] = [];
  for (let d = dayStart; d <= dayEnd; d++) {
    result.push({
      dayNumber: d,
      status:    statusMap.get(d) ?? 'not_started',
    });
  }
  return result;
}
